import { useMutationHook, useQueryHook } from "@/hooks"
import { ANALYSIS_CACHE } from "@/hooks/api/queryCache"
import {
  convertLegacyReportTemplate,
  seedLegacyStatsSort,
} from "@/app/report/common/legacyStatsSection"
import {
  readActivityIntervalSummary,
  readDailyCSVData,
  readPlayerActivitySummary,
  readPlayerSessionSummary,
  readReportTemplateDetail,
  readReportTemplates,
  readSessionIntervalSummary,
  readSessionPlayerSummary,
  readTrendPlayerActivitySummary,
  readTrendPlayerCSVData,
  readTrendPlayerSummary,
  readTrendGroupSummary,
  readDailySessionGroupSummary,
  readDailyActivityGroupSummary,
  upsertReportTemplate,
  updateReportTemplateLastUsed,
  readPlayerActivityActionDetails,
  readPlayerSessionActionDetails,
  readPlayerActivityAction18Zone,
  readPlayerSessionAction18Zone,
  readPlayerActivityLocation18Zone,
  readPlayerSessionLocation18Zone,
  readPlayerActivityLocationHeatmap,
  readPlayerSessionLocationHeatmap,
  readPlayerActivityFormationMap,
  readPlayerSessionFormationMap,
  readPlayerActivityPerformance,
  readPlayerActivityActions,
} from "@/app/api/report"
import {
  ChartSessionPlayerSummary,
  ReadActivityIntervalSummaryParams,
  ReadActivityIntervalSummaryResponse,
  ReadDailyCSVDataParams,
  ReadPlayerActivitySummaryParams,
  ReadPlayerActivitySummaryResponse,
  ReadPlayerSessionSummaryParams,
  ReadPlayerSessionSummaryResponse,
  ReadReportTemplateDetailResponse,
  ReadReportTemplatesParams,
  ReadSessionIntervalSummaryParams,
  ReadSessionIntervalSummaryResponse,
  ReadSessionPlayerSummaryParams,
  ReadSessionPlayerSummaryResponse,
  ReadTrendPlayerActivitySummaryParams,
  ReadTrendPlayerActivitySummaryResponse,
  ReadTrendPlayerCSVDataParams,
  ReadTrendPlayerSummaryParams,
  ReadTrendPlayerSummaryResponse,
  ReadPlayerActivityActionDetailsParams,
  ReadPlayerSessionActionDetailsParams,
  ReadPlayerActivityActionDetailsResponse,
  ReadPlayerSessionActionDetailsResponse,
  ReadPlayerActivityAction18ZoneParams,
  ReadPlayerActivityAction18ZoneResponse,
  ReadPlayerActivityLocation18ZoneParams,
  ReadPlayerActivityLocationHeatmapParams,
  ReadPlayerActivityLocationHeatmapResponse,
  ReadPlayerActivityLocation18ZoneResponse,
  ReadPlayerActivityFormationMapParams,
  ReadPlayerActivityFormationMapResponse,
  ReadPlayerSessionAction18ZoneParams,
  ReadPlayerSessionAction18ZoneResponse,
  ReadPlayerSessionFormationMapParams,
  ReadPlayerSessionFormationMapResponse,
  ReadPlayerSessionLocation18ZoneParams,
  ReadPlayerSessionLocation18ZoneResponse,
  ReadPlayerSessionLocationHeatmapParams,
  ReadPlayerSessionLocationHeatmapResponse,
  ReadPlayerPerformanceParams,
  ReadPlayerPerformanceResponse,
  ReadPlayerPerformanceActionsParams,
  ReadPlayerPerformanceActionsResponse,
  ReadTrendGroupSummaryParams,
  ReadDailySessionGroupSummaryParams,
  ReadDailyActivityGroupSummaryParams,
  ReadTrendGroupSummaryResponse,
} from "@/types/report"
import { UseQueryOptions } from "@tanstack/react-query"
import { ApiError } from "@/app/api/type"

export const useSessionPlayerSummaryQuery = (
  params: ReadSessionPlayerSummaryParams,
  enabled: boolean,
) => {
  return useQueryHook<ChartSessionPlayerSummary[], unknown>({
    queryKey: ["readSessionPlayerSummary", ...Object.values(params)],
    queryFn: () => readSessionPlayerSummary(params),
    options: {
      ...ANALYSIS_CACHE,
      enabled,
    } as UseQueryOptions<ReadSessionPlayerSummaryResponse, ApiError>,
  })
}

export const usePlayerSessionSummaryQuery = (
  params: ReadPlayerSessionSummaryParams,
  enabled: boolean,
  additionalKey: string[] = [],
) => {
  return useQueryHook({
    queryKey: ["readPlayerSessionSummary", ...Object.values(params), ...additionalKey],
    queryFn: () => readPlayerSessionSummary(params),
    options: {
      ...ANALYSIS_CACHE,
      enabled,
    } as UseQueryOptions<ReadPlayerSessionSummaryResponse, ApiError>,
  })
}

export const usePlayerActivitySummaryQuery = (
  params: ReadPlayerActivitySummaryParams,
  enabled: boolean,
) => {
  return useQueryHook({
    queryKey: ["readPlayerActivitySummary", ...Object.values(params)],
    queryFn: () => readPlayerActivitySummary(params),
    options: {
      ...ANALYSIS_CACHE,
      enabled,
    } as UseQueryOptions<ReadPlayerActivitySummaryResponse, ApiError>,
  })
}

export const useActivityIntervalSummaryQuery = (
  params: ReadActivityIntervalSummaryParams,
  enabled: boolean,
) => {
  return useQueryHook({
    queryKey: ["readActivityIntervalSummary", ...Object.values(params)],
    queryFn: () => readActivityIntervalSummary(params),
    options: {
      ...ANALYSIS_CACHE,
      enabled,
    } as UseQueryOptions<ReadActivityIntervalSummaryResponse, ApiError>,
  })
}

export const useSessionIntervalSummaryQuery = (
  params: ReadSessionIntervalSummaryParams,
  enabled: boolean,
) => {
  return useQueryHook({
    queryKey: ["readSessionIntervalSummary", ...Object.values(params)],
    queryFn: () => readSessionIntervalSummary(params),
    options: {
      ...ANALYSIS_CACHE,
      enabled,
    } as UseQueryOptions<ReadSessionIntervalSummaryResponse, ApiError>,
  })
}

export const useDailyCSVDataQuery = (params: ReadDailyCSVDataParams) => {
  return useQueryHook({
    queryKey: ["readActivityIntervalSummary", ...Object.values(params)],
    queryFn: () => readDailyCSVData(params),
  })
}

export const useTrendPlayerCSVDataQuery = (params: ReadTrendPlayerCSVDataParams) => {
  return useQueryHook({
    queryKey: ["readActivityIntervalSummary", ...Object.values(params)],
    queryFn: () => readTrendPlayerCSVData(params),
  })
}

export const useTrendPlayerSummaryQuery = (
  params: ReadTrendPlayerSummaryParams,
  enabled: boolean,
) => {
  return useQueryHook({
    queryKey: ["readTrendPlayerSummary", ...Object.values(params)],
    queryFn: () => readTrendPlayerSummary(params),
    options: {
      ...ANALYSIS_CACHE,
      enabled,
    } as UseQueryOptions<ReadTrendPlayerSummaryResponse, ApiError>,
  })
}

export const useTrendPlayerActivitySummaryQuery = (
  params: ReadTrendPlayerActivitySummaryParams,
  enabled?: boolean,
) => {
  return useQueryHook<ReadTrendPlayerActivitySummaryResponse, ReadTrendPlayerActivitySummaryParams>(
    {
      queryKey: ["readTrendPlayerActivitySummary", ...Object.values(params)],
      queryFn: () => readTrendPlayerActivitySummary(params),
      options: {
        ...ANALYSIS_CACHE,
        enabled,
      } as UseQueryOptions<ReadTrendPlayerActivitySummaryResponse, ApiError>,
    },
  )
}

export const useReportTemplateMetasQuery = (
  params: ReadReportTemplatesParams,
  enabled: boolean = true,
) => {
  return useQueryHook({
    queryKey: ["readReportTemplateMetas", ...Object.values(params)],
    queryFn: () => readReportTemplates(params),
    options: { enabled },
  })
}

export const useReportTemplateDetailQuery = (idfReportTemplate?: number) => {
  return useQueryHook({
    queryKey: ["readReportTemplateDetail", idfReportTemplate],
    // 폐지 예정 Stats 섹션은 읽는 시점에 Custom Table 로 대치한다 — 리포트·PDF·설정·일괄 CSV 공통 진입점 (#99)
    queryFn: async () => {
      const res = await readReportTemplateDetail(idfReportTemplate!)
      if (!res.success || !res.response) {
        return res
      }
      seedLegacyStatsSort(res.response)

      return { ...res, response: convertLegacyReportTemplate(res.response) }
    },
    options: { enabled: !!idfReportTemplate } as UseQueryOptions<
      ReadReportTemplateDetailResponse,
      ApiError
    >,
  })
}

// 액션 상세(궤적 포함) 응답을 같은 파라미터로 잠시 재사용하는 시간 — PDF 빌더 Action Map 이 인스턴스마다 다시 받지 않게
const ACTION_DETAILS_STALE_MS = 5 * 60 * 1000
const ACTION_DETAILS_GC_MS = 10 * 60 * 1000

export const useReportPlayerActivityActionDetailsQuery = (
  params: ReadPlayerActivityActionDetailsParams,
) => {
  return useQueryHook({
    queryKey: ["readPlayerActivityActionDetails", ...Object.values(params)],
    queryFn: () => readPlayerActivityActionDetails(params),
    options: {
      ...ANALYSIS_CACHE,
      enabled:
        !!params.players?.length && !!params.actionType && !!params.activities && params.isActivity,
      // 액션 상세는 궤적까지 실려 무겁다. PDF 빌더의 Action Map 은 맵·표·세션 아이템마다 같은 범위로 조회하므로
      // 같은 파라미터면 잠시 재사용한다 (전역 기본값은 staleTime 0·gcTime 0 이라 인스턴스마다 다시 받는다)
      staleTime: ACTION_DETAILS_STALE_MS,
      gcTime: ACTION_DETAILS_GC_MS,
    } as UseQueryOptions<ReadPlayerActivityActionDetailsResponse, ApiError>,
  })
}

export const useReportPlayerSessionActionDetailsQuery = (
  params: ReadPlayerSessionActionDetailsParams,
) => {
  return useQueryHook({
    queryKey: ["readPlayerSessionActionDetails", ...Object.values(params)],
    queryFn: () => readPlayerSessionActionDetails(params),
    options: {
      ...ANALYSIS_CACHE,
      enabled:
        !!params.players?.length &&
        !!params.sessions?.length &&
        !!params.actionType &&
        params.isSession,
      staleTime: ACTION_DETAILS_STALE_MS,
      gcTime: ACTION_DETAILS_GC_MS,
    } as UseQueryOptions<ReadPlayerSessionActionDetailsResponse, ApiError>,
  })
}

export const useReportPlayerActivityAction18ZoneQuery = (
  params: ReadPlayerActivityAction18ZoneParams,
) => {
  return useQueryHook({
    queryKey: ["readPlayerActivityAction18Zone", ...Object.values(params)],
    queryFn: () => readPlayerActivityAction18Zone(params),
    options: {
      ...ANALYSIS_CACHE,
      enabled:
        params.mapType === "action18Zone" &&
        !!params.players?.length &&
        !!params.actionType &&
        !!params.activities &&
        params.isActivity,
    } as UseQueryOptions<ReadPlayerActivityAction18ZoneResponse, ApiError>,
  })
}

export const useReportPlayerSessionAction18ZoneQuery = (
  params: ReadPlayerSessionAction18ZoneParams,
) => {
  return useQueryHook({
    queryKey: ["readPlayerSessionAction18Zone", ...Object.values(params)],
    queryFn: () => readPlayerSessionAction18Zone(params),
    options: {
      ...ANALYSIS_CACHE,
      enabled:
        params.mapType === "action18Zone" &&
        !!params.players?.length &&
        !!params?.sessions.length &&
        !!params.actionType &&
        params.isSession,
    } as UseQueryOptions<ReadPlayerSessionAction18ZoneResponse, ApiError>,
  })
}

export const useReportPlayerActivityFormationMapQuery = (
  params: ReadPlayerActivityFormationMapParams,
) => {
  return useQueryHook({
    queryKey: ["readPlayerActivityFormationMap", ...Object.values(params)],
    queryFn: () => readPlayerActivityFormationMap(params),
    options: {
      ...ANALYSIS_CACHE,
      enabled:
        params.mapType === "formationMap" &&
        !!params.players?.length &&
        !!params.activities &&
        params.isActivity,
    } as UseQueryOptions<ReadPlayerActivityFormationMapResponse, ApiError>,
  })
}

export const useReportPlayerSessionFormationMapQuery = (
  params: ReadPlayerSessionFormationMapParams,
) => {
  return useQueryHook({
    queryKey: ["readPlayerSessionFormationMap", ...Object.values(params)],
    queryFn: () => readPlayerSessionFormationMap(params),
    options: {
      ...ANALYSIS_CACHE,
      enabled:
        params.mapType === "formationMap" &&
        !!params.sessions?.length &&
        !!params.players?.length &&
        params.isSession,
    } as UseQueryOptions<ReadPlayerSessionFormationMapResponse, ApiError>,
  })
}

export const useReportPlayerActivityLocation18ZoneQuery = (
  params: ReadPlayerActivityLocation18ZoneParams,
) => {
  return useQueryHook({
    queryKey: ["readPlayerActivityLocation18Zone", ...Object.values(params)],
    queryFn: () => readPlayerActivityLocation18Zone(params),
    options: {
      ...ANALYSIS_CACHE,
      enabled:
        params.mapType === "location18Zone" &&
        !!params.players?.length &&
        !!params.activities &&
        params.isActivity,
    } as UseQueryOptions<ReadPlayerActivityLocation18ZoneResponse, ApiError>,
  })
}

export const useReportPlayerSessionLocation18ZoneQuery = (
  params: ReadPlayerSessionLocation18ZoneParams,
) => {
  return useQueryHook({
    queryKey: ["readPlayerSessionLocation18Zone", ...Object.values(params)],
    queryFn: () => readPlayerSessionLocation18Zone(params),
    options: {
      ...ANALYSIS_CACHE,
      enabled:
        params.mapType === "location18Zone" &&
        !!params.sessions?.length &&
        !!params.players?.length &&
        params.isSession,
    } as UseQueryOptions<ReadPlayerSessionLocation18ZoneResponse, ApiError>,
  })
}

export const useReportPlayerActivityLocationHeatmapQuery = (
  params: ReadPlayerActivityLocationHeatmapParams,
) => {
  return useQueryHook({
    queryKey: ["readPlayerActivityLocationHeatmap", ...Object.values(params)],
    queryFn: () => readPlayerActivityLocationHeatmap(params),
    options: {
      ...ANALYSIS_CACHE,
      enabled:
        params.mapType === "locationHeatMap" &&
        !!params.players?.length &&
        !!params.activities &&
        params.isActivity,
    } as UseQueryOptions<ReadPlayerActivityLocationHeatmapResponse, ApiError>,
  })
}

export const useReportPlayerSessionLocationHeatmapQuery = (
  params: ReadPlayerSessionLocationHeatmapParams,
) => {
  return useQueryHook({
    queryKey: ["readPlayerSessionLocationHeatmap", ...Object.values(params)],
    queryFn: () => readPlayerSessionLocationHeatmap(params),
    options: {
      ...ANALYSIS_CACHE,
      enabled:
        params.isSession &&
        params.mapType === "locationHeatMap" &&
        !!params.sessions?.length &&
        !!params.players?.length,
    } as UseQueryOptions<ReadPlayerSessionLocationHeatmapResponse, ApiError>,
  })
}

export const useReportPlayerActivityPerformanceQuery = (
  params: ReadPlayerPerformanceParams,
  enabled = true,
) => {
  return useQueryHook({
    queryKey: ["readPlayerActivityPerformance", ...Object.values(params)],
    queryFn: () => readPlayerActivityPerformance(params),
    options: {
      ...ANALYSIS_CACHE,
      enabled: enabled && !!params.idfActivity && !!params.idfPlayer && !!params.dataTypes?.length,
    } as UseQueryOptions<ReadPlayerPerformanceResponse, ApiError>,
  })
}

export const useReportPlayerActivityActionsQuery = (
  params: ReadPlayerPerformanceActionsParams,
  enabled = true,
) => {
  return useQueryHook({
    queryKey: ["readPlayerActivityActions", ...Object.values(params)],
    queryFn: () => readPlayerActivityActions(params),
    options: {
      ...ANALYSIS_CACHE,
      enabled:
        enabled && !!params.idfActivity && !!params.idfPlayer && !!params.actionTypes?.length,
    } as UseQueryOptions<ReadPlayerPerformanceActionsResponse, ApiError>,
  })
}

export const useTrendGroupSummaryQuery = (
  params: ReadTrendGroupSummaryParams,
  enabled?: boolean,
) => {
  return useQueryHook<ReadTrendGroupSummaryResponse, ReadTrendGroupSummaryParams>({
    queryKey: ["readTrendGroupSummary", ...Object.values(params)],
    queryFn: () => readTrendGroupSummary(params),
    options: {
      ...ANALYSIS_CACHE,
      enabled: enabled && !!params.idfActivityList?.length && !!params.groupKey1,
    } as UseQueryOptions<ReadTrendGroupSummaryResponse, ApiError>,
  })
}

export const useDailySessionGroupSummaryQuery = (
  params: ReadDailySessionGroupSummaryParams,
  enabled?: boolean,
) => {
  return useQueryHook<ReadTrendGroupSummaryResponse, ReadDailySessionGroupSummaryParams>({
    queryKey: ["readDailySessionGroupSummary", ...Object.values(params)],
    queryFn: () => readDailySessionGroupSummary(params),
    options: {
      ...ANALYSIS_CACHE,
      enabled: enabled && !!params.idfSessionList?.length && !!params.groupKey1,
    } as UseQueryOptions<ReadTrendGroupSummaryResponse, ApiError>,
  })
}

/* Daily 리포트 액티비티 모드의 Custom Table — 액티비티 전체 기준(세션 크롭 미반영) (#104) */
export const useDailyActivityGroupSummaryQuery = (
  params: ReadDailyActivityGroupSummaryParams,
  enabled?: boolean,
) => {
  return useQueryHook<ReadTrendGroupSummaryResponse, ReadDailyActivityGroupSummaryParams>({
    queryKey: ["readDailyActivityGroupSummary", ...Object.values(params)],
    queryFn: () => readDailyActivityGroupSummary(params),
    options: {
      ...ANALYSIS_CACHE,
      enabled: enabled && !!params.idfActivityList?.length && !!params.groupKey1,
    } as UseQueryOptions<ReadTrendGroupSummaryResponse, ApiError>,
  })
}

export const useUpsertReportTemplateMutation = () => {
  return useMutationHook(upsertReportTemplate)
}

export const useUpdateReportTemplateLastUsedMutation = () => {
  return useMutationHook(
    ({
      idfReportTemplate,
      reportType,
    }: {
      idfReportTemplate: number
      reportType: "daily" | "trend"
    }) => updateReportTemplateLastUsed(idfReportTemplate, reportType),
  )
}
